import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../lib/api";
import { useUserAuth } from "../context/UserAuthContext";

/**
 * OAuth landing route. Reads the session_id from URL fragment,
 * exchanges it via the backend, then navigates to /account.
 * The `session_id` in the URL fragment is intentionally never sent to /me.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useUserAuth() || {};
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Idempotent under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/auth/session`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Session-ID": sessionId },
        });
        if (!res.ok) throw new Error("session-exchange-failed");
        const user = await res.json();
        // Clear the hash so future refreshes don't re-trigger the exchange
        window.history.replaceState({}, document.title, window.location.pathname);
        if (refresh) await refresh();
        navigate("/account", { replace: true, state: { user } });
      } catch (_e) {
        navigate("/login-failed", { replace: true });
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand" data-testid="auth-callback">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <div className="w-10 h-10 rounded-full border-2 border-forest border-t-transparent animate-spin mx-auto mb-6" />
        <p className="text-xs uppercase tracking-[0.3em] text-stone">Signing you in…</p>
      </motion.div>
    </div>
  );
}
